"use client";

import Link from "next/link";
import { useContext } from "react";
import { MdVerified } from "react-icons/md";

import { InfoTooltip } from "@/components/shared/info-tooltip";
import { UserContext } from "@/contexts/user-context";
import { useUserSocialProfileQuery } from "@/hooks/use-user-social-profile-query";
import { ProfileSocialsMap } from "@/utils/constants";
import { getUserName } from "@/utils/usernames";

import { toast } from "../ui/use-toast";

type UserConnectedSocialsListProps = {
  isMyAccount?: boolean;
  publicKey: string;
};

const UserConnectedSocialsList = ({
  publicKey,
  isMyAccount,
}: UserConnectedSocialsListProps) => {
  const { userSocialProfiles } = useContext(UserContext);

  const { data: fetchedSocialProfile, error } = useUserSocialProfileQuery(
    publicKey,
    {
      enabled: !isMyAccount && !!publicKey,
    },
  );

  if (error) {
    toast({
      variant: "destructive",
      title: "Error",
      description: `An error occurred while getting social profiles`,
    });
  }

  const socialProfiles = isMyAccount
    ? userSocialProfiles
    : fetchedSocialProfile;

  return (
    <div className="my-0 mr-auto flex gap-2">
      <div className="flex flex-row gap-1">
        {socialProfiles &&
          Object.entries(socialProfiles).map(([key, value]) => {
            if (
              value === null ||
              Object.keys(value).length === 0 ||
              value?.Username === ""
            ) {
              return null;
            }
            const socialKey = key as keyof typeof ProfileSocialsMap;

            const SocialIcon = ProfileSocialsMap[socialKey].icon;
            const url = `${ProfileSocialsMap[socialKey].url}/${value.Username}`;

            return (
              <div key={key}>
                <InfoTooltip
                  text={getUserName({
                    account: {
                      username: value.Username,
                    },
                  })}
                >
                  <Link href={url} className="my-1" target="_blank">
                    <figure className="relative ml-auto pr-4">
                      <SocialIcon className="size-5" />
                      {value?.Verified && (
                        <MdVerified
                          size={13}
                          className="
                            absolute top-[-2px] right-[5px] text-xl
                            text-blue-400
                          "
                        />
                      )}
                    </figure>
                  </Link>
                </InfoTooltip>
              </div>
            );
          })}
      </div>
    </div>
  );
};

export default UserConnectedSocialsList;
